import { isArray, isEmpty } from 'lodash';
import { useCallback, useEffect } from 'react';
import { atom, useRecoilState } from 'recoil';
import { stringify, parse } from 'query-string';

import { useQueryParams } from '@/hooks';
import { localStorageEffect } from '@/utils/recoil';
import storage from '@/utils/storage1';

const PostFilterAtom = atom({
  key: 'postFilter',
  default: storage.post_filter || {},
  effects_UNSTABLE: [localStorageEffect('post_filter')],
});

export type Filters = {
  types?: string[];
  creator_usd_value?: string;
  create_at?: string;
};

export const usePostFilter = () => {
  const [filters, _setPostFilter] = useRecoilState<Filters>(PostFilterAtom);

  const [, setFiltersQuery] = useQueryParams<string | undefined>('filter');

  const setPostFilter = useCallback(
    (_filters: Filters) => {
      const filters = Object.fromEntries(
        Object.entries(_filters).filter(([, value]) => {
          // 过滤掉值为空的键值对
          if (isArray(value)) {
            return !isEmpty(value);
          }
          return value !== null && value !== undefined && value !== '';
        }),
      );
      _setPostFilter(filters);
      if (isEmpty(filters)) {
        setFiltersQuery(undefined);
        return;
      }
      let queryString;
      try {
        queryString = stringify(filters, { arrayFormat: 'bracket' });
      } catch (e) {
        queryString = '';
      }
      setFiltersQuery(queryString);
    },
    [_setPostFilter, setFiltersQuery],
  );

  const clearFilter = useCallback(() => {
    _setPostFilter({});
    setFiltersQuery(undefined);
  }, [_setPostFilter, setFiltersQuery]);

  return { filters, setPostFilter, clearFilter };
};

export const useInitPostFilter = () => {
  const { filters, setPostFilter } = usePostFilter();
  const [filterQueryString] = useQueryParams<string | undefined>('filter');
  useEffect(() => {
    if (!filterQueryString) {
      if (filters) {
        // url上没有filter参数，本地有缓存的情况，同步到url上
        setPostFilter(filters);
      }
      return;
    }
    let filterQuery = {};
    try {
      filterQuery =
        (filterQueryString.includes('"')
          ? JSON.parse(filterQueryString)
          : parse(filterQueryString, { arrayFormat: 'bracket' })) || {};
    } catch {}
    if (!isEmpty(filterQuery)) {
      // url上有filter参数，不管本地是否有缓存，都使用url上的参数
      setPostFilter(filterQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
